import { Badge } from '@/components/ui/badge';
import { Wifi, WifiOff } from 'lucide-react';
import useOrderStatusStream from '../../hooks/useOrderStatusStream';
import PushOptInButton from './PushOptInButton';

const STATUS_LABEL = {
  pending: 'Pending payment',
  confirmed: 'Confirmed',
  preparing: 'Being prepared',
  ready: 'Ready for pickup',
  picked_up: 'Picked up',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

const STATUS_COLOR = {
  pending: 'bg-slate-500',
  confirmed: 'bg-blue-500',
  preparing: 'bg-amber-500',
  ready: 'bg-emerald-500',
  picked_up: 'bg-emerald-600',
  delivered: 'bg-emerald-700',
  cancelled: 'bg-red-500',
};

/**
 * LiveOrderStatus — Cluster 6 · P7.
 *
 * Subscribes to the order's SSE stream and renders the current status
 * + a small connection indicator. Falls back to nothing (safe) if the
 * browser doesn't support EventSource; parent should still show the
 * polled status.
 */
export default function LiveOrderStatus({ orderId, fallbackStatus }) {
  const { status, connected, error } = useOrderStatusStream(orderId);
  const live = status?.status || fallbackStatus;
  const readyBy = status?.confirmed_ready_by;
  const label = STATUS_LABEL[live] || live || 'Unknown';
  return (
    <div className="flex items-center gap-2" data-testid={`live-order-status-${orderId}`}>
      <Badge className={STATUS_COLOR[live] || 'bg-slate-500'}>{label}</Badge>
      {readyBy && (
        <span className="text-xs text-slate-500" data-testid={`live-order-ready-${orderId}`}>
          Ready by {new Date(readyBy).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      )}
      {connected ? (
        <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 uppercase tracking-wider"
              data-testid="live-order-connected">
          <Wifi className="w-3 h-3" /> live
        </span>
      ) : error ? (
        <span className="inline-flex items-center gap-1 text-[10px] text-slate-400 uppercase tracking-wider"
              title={error}
              data-testid="live-order-disconnected">
          <WifiOff className="w-3 h-3" /> offline
        </span>
      ) : null}
      {/* Push opt-in — Cluster 6 · P7 enhancement. Lets the DAC get a
          phone-lock-screen notification the instant the order flips to
          "ready", so they don't need to keep the tab open. */}
      <PushOptInButton orderId={orderId} />
    </div>
  );
}
